import {List} from 'immutable';

import {
  KpiAssignee,
  KpiAssignmentId,
  KpiOrderBy,
  KpiTargetId,
  KpiTargetTree,
} from '@store/@types/kpi';
import {createSelector} from '@store/@common';
import {getComparator, compareStrings} from '@store/@common/sorting';
import {getUserById} from '@store/user';

import KpiGoalStore from './KpiTargetStore';
import {frequencyComparator} from './kpi.utils';

const baseKpiState = createSelector(KpiGoalStore);

export const assignmentsByKpiId = createSelector([baseKpiState], kpi => kpi.assignmentsByKpiId);

export const selectedKpis = createSelector([baseKpiState], kpi => kpi.selectedKpis);

export const selectedGoal = createSelector([baseKpiState], kpi => kpi.selectedGoal);

export const kpiTargetTree = createSelector([baseKpiState], kpi => kpi.kpiTargetTree);

export const kpiTargetTreeSorted = createSelector([baseKpiState, getUserById], (kpi, getUser) => {
  const order = kpi.order;
  const orderBy = kpi.orderBy;
  const getAssignments = kpi.assignmentsByKpiId;

  return kpi.kpiTargetTree.sort(
    getComparator<KpiTargetTree, KpiOrderBy>(orderBy, order, {
      default: (l, r) => compareStrings(l.kpiTarget[orderBy], r.kpiTarget[orderBy]),
      frequency: (l, r) => frequencyComparator(l.kpiTarget.frequency, r.kpiTarget.frequency),
      assignments: (l, r) =>
        getAssignments.get(l.kpiTarget.id, List()).size -
        getAssignments.get(r.kpiTarget.id, List()).size,
      createdByUserId: (l, r) => {
        const userL = getUser(l.kpiTarget.createdByUserId);
        const userR = getUser(r.kpiTarget.createdByUserId);
        return compareStrings(userL?.getDisplayName(), userR?.getDisplayName());
      },
    })
  );
});

export const getTreeById = createSelector([kpiTargetTree], (tree: List<KpiTargetTree>) => id => {
  const idNum = parseInt(id, 10);
  if (!isNaN(idNum)) {
    return tree.reduce<undefined | Partial<KpiTargetTree>>(
      (res, cur) => {
        if (res?.kpiTarget) {
          return res;
        }
        if (cur.kpiTarget?.id === idNum) {
          return cur;
        }
        const kpiTarget = cur.kpiChildren.find(child => child.id === idNum);
        return {kpiTarget, kpiChildren: List()} as KpiTargetTree;
      },
      {kpiTarget: undefined, kpiChildren: List()}
    );
  }

  return {kpiTarget: undefined, kpiChildren: List()};
});

export const kpisById = createSelector([baseKpiState], store => store.kpisById);

export const selectedKpiTargets = createSelector([selectedKpis, kpiTargetTree], (ids, kpiTree) => {
  return kpiTree.filter(kpi => ids.includes(kpi.kpiTarget.id)).map(el => el.kpiTarget);
});

export const hasKpiAssigned = createSelector(
  [baseKpiState],
  store => (kpiId: KpiTargetId, assignee: KpiAssignee, assignmentId?: KpiAssignmentId) =>
    store.assignments
      .filter(a => a.id !== assignmentId && a.kpiId === kpiId)
      .some(a => a.assignee.id === assignee.id)
);

export const getAssignment = createSelector(
  [baseKpiState],
  store => id => store.assignments.find(a => a.id === id)
);
